import { useContext } from 'react'

import {
  Box, Button, ModalContext, SidebarModal, SortableList,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import useCampaignQuoteTemplate from '@hooks/useCampaignQuoteTemplate'
import useCampaignQuoteTemplateSections from '@hooks/useCampaignQuoteTemplateSections'
import useCampaignQuoteTemplateSection from '@hooks/useCampaignQuoteTemplateSection'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useEditCampaignQuoteTemplateSectionSort from './hooks/useEditCampaignQuoteTemplateSectionSort'

import DraggableCampaignQuoteTemplateSection from './components/DraggableCampaignQuoteTemplateSection'

const MODAL_KEY = 'EditCampaignQuoteTemplateSectionSortModal'

type ModalCallbacks = {
  closeModal: () => void,
  updateCampaignQuoteTemplateSectionSort: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const EditCampaignQuoteTemplateSectionSortModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, updateCampaignQuoteTemplateSectionSort } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { campaignQuoteTemplate: initCampaignQuoteTemplate } = modalPayload

  const { campaignQuoteTemplate, updating } = useCampaignQuoteTemplate(initCampaignQuoteTemplate)

  const campaignQuoteSectionsPayload = useCampaignQuoteTemplateSections({
    filters: {
      campaign_quote_template_id: campaignQuoteTemplate?.id,
    },
  })
  const { filteredCampaignQuoteTemplateSections } = campaignQuoteSectionsPayload

  const sortPayload = useEditCampaignQuoteTemplateSectionSort({ filteredCampaignQuoteTemplateSections })
  const {
    callbacks: { modifyCampaignQuoteTemplateSectionSort },
    sortedIds,
  } = sortPayload

  const {
    callbacks: { updateQuoteTemplateSectionOrder },
  } = useCampaignQuoteTemplateSection({})

  // To be used with handleCallbackAction
  const updateQuoteTemplateSectionSortPayload: HandleCallbackActionParams = {
    callbacks: {
      // @ts-expect-error TS2322
      action: updateQuoteTemplateSectionOrder,
      afterAction: closeModal,
    },
    entityParams: {
      campaignQuoteTemplateId: campaignQuoteTemplate.id,
      sortedQuoteTemplateSectionIds: sortedIds,
    },
    toastText: 'Quote Template Sections Sorted',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Sort" titleSecondLine="Quote Sections" />

      <SidebarModal.Content>
        <Box flexDirection="column">
          <SortableList
            callbacks={{
              onDragEnd: (fromIndex, toIndex, payload) => {
                // @ts-expect-error TS2345
                modifyCampaignQuoteTemplateSectionSort(fromIndex, toIndex, payload)
              },
            }}
          >
            <SortableList.Dropzone droppableId="sortedIds">
              <Box flexDirection="column">
                {sortedIds.map((id, index) => (
                  <SortableList.Item draggableId={`CampaignQuoteTemplateSection-${id}`} key={id} index={index}>
                    {/*
                     // @ts-expect-error TS2322 */}
                    <DraggableCampaignQuoteTemplateSection campaignQuoteTemplateSectionId={id} />
                  </SortableList.Item>
                ))}
              </Box>
            </SortableList.Dropzone>
          </SortableList>
        </Box>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          loading={updating}
          onClick={() => updateCampaignQuoteTemplateSectionSort(updateQuoteTemplateSectionSortPayload)}
          size="large"
        >
          Save Sort
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <EditCampaignQuoteTemplateSectionSortModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
